// Reports' panels (spec 044 D90, D93, D126): Lessons over time, by subject (Figma "Income
// over time", 11661:146231) and the two audience cards, Users by country (11541:96657) and
// Users by gender (11632:175509). Each takes `state` — "loading", "failed", "empty"
// (buildReportStats has not run yet) or "ready".
import React, { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { RiArrowRightSLine } from "@remixicon/react";
import { WorldMap } from "../../components/kit/WorldMap";
import { ErrorState } from "../../components/kit/States";
import { ChartTooltip } from "../../components/kit/ChartTooltip";
import { ChangeChip } from "../../components/kit/KpiCard";
import { focusRing, tokens } from "../../utils/figmaTokens";
import { countText, ringSlicePath } from "../../utils/kitPresentation";
import { barLabel, countryCard, genderCard, lessonsText, subjectSubtitle } from "../../utils/reportCharts";
import FLAGS from "../../utils/flagLoaders";

// Figma's padding (32 px for the chart, 24 for the audience cards), 16 on a phone.
export const cardSx = (pad, gap) => ({
  bgcolor: tokens.surface.default,
  border: `1px solid ${tokens.border.default}`,
  borderRadius: "16px",
  p: { xs: "16px", sm: pad },
  display: "flex",
  flexDirection: "column",
  gap,
  minWidth: 0,
});
const titleSx = { fontSize: 21, lineHeight: "28px", fontWeight: 600, letterSpacing: "-0.2px", color: tokens.text.primary };

export const Head = ({ id, title, subtitle, action }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
      <Typography id={id} component="h2" sx={titleSx}>
        {title}
      </Typography>
      <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
        {subtitle}
      </Typography>
    </Box>
    {action}
  </Box>
);

// What a panel shows instead of its body until the summaries are in.
export const NotReady = ({ state, height, onRetry }) => {
  if (state === "loading") return <Skeleton variant="rounded" height={height} />;
  if (state === "failed") {
    return <ErrorState compact title="The charts could not load" message="Try again in a moment." onRetry={onRetry} />;
  }
  return (
    <Typography variant="bodyMedium" sx={{ color: tokens.text.secondary, py: "24px" }}>
      The chart fills in within the hour.
    </Typography>
  );
};

const Meter = ({ fill, color }) => (
  <Box aria-hidden sx={{ flex: 1, minWidth: 40, height: 8, borderRadius: "4px", bgcolor: tokens.surface.muted, overflow: "hidden" }}>
    <Box sx={{ width: `${Math.round(fill * 1000) / 10}%`, height: "100%", borderRadius: "4px", bgcolor: color }} />
  </Box>
);

// D126–D129: Lessons over time, by subject — the last 12 months, one bar a month stacking its
// lessons that are not cancelled by subject (Figma's order: Quran on top), the legend, and the
// chip against the 12 months before. A bar shows its numbers on hover or focus.
const SUBJECT_FILL = {
  quran: tokens.chart.series.quran,
  arabic: tokens.chart.series.arabic,
  islamic: tokens.chart.series.islamic,
  other: tokens.chart.track,
};
const PLOT = 381; // Figma's plot height (11661:146248)
const SEAM = 2; // between a bar's parts
const axisSx = { fontSize: 10, lineHeight: "14px", fontWeight: 500, letterSpacing: "0.3px", color: tokens.text.tertiary };

export const SubjectPanel = ({ state, chart, updated, onRetry }) => {
  const [hover, setHover] = useState(null);
  const ready = state === "ready";
  const top = ready ? chart.ticks[chart.ticks.length - 1] : 1;
  const heightOf = (bar) => Math.round((bar.total / top) * PLOT);
  const shown = ready && hover != null ? chart.bars[hover] : null;
  return (
    <Box component="section" aria-labelledby="reports-subjects-title" sx={cardSx("18px", "14px")}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: "12px 24px" }}>
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography id="reports-subjects-title" component="h2" sx={titleSx}>
            Lessons over time
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {ready && chart.chip && <ChangeChip chip={chart.chip} />}
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
              {subjectSubtitle(ready ? chart : null, updated)}
            </Typography>
          </Box>
        </Box>
        {ready && (
          <Box component="ul" aria-label="Subjects" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexWrap: "wrap", gap: "8px 14px" }}>
            {chart.legend.map((s) => (
              <Box component="li" key={s.name} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: SUBJECT_FILL[s.tone] }} />
                <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
                  {s.name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {ready ? (
        <Box sx={{ bgcolor: tokens.chart.well, borderRadius: "12px", p: "12px" }}>
          <Box sx={{ position: "relative", pl: "52px" }}>
            {chart.ticks.map((t) => {
              const y = PLOT - (t / top) * PLOT;
              return (
                <React.Fragment key={t}>
                  <Box aria-hidden sx={{ position: "absolute", left: 52, right: 0, top: y, height: "1px", bgcolor: tokens.chart.grid }} />
                  <Typography aria-hidden sx={{ ...axisSx, position: "absolute", left: 0, width: 42, top: y - 7, textAlign: "right" }}>
                    {countText(t)}
                  </Typography>
                </React.Fragment>
              );
            })}
            <Box sx={{ position: "relative", height: PLOT, display: "flex", gap: { xs: "4px", sm: "8px" } }}>
              {chart.bars.map((bar, i) => {
                const parts = bar.parts.filter((x) => x.count);
                const room = Math.max(0, heightOf(bar) - SEAM * (parts.length - 1));
                return (
                  <Box
                    key={bar.key}
                    tabIndex={0}
                    role="img"
                    aria-label={barLabel(bar)}
                    onMouseEnter={() => setHover(i)}
                    onFocus={() => setHover(i)}
                    onMouseLeave={() => setHover(null)}
                    onBlur={() => setHover(null)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: `${SEAM}px`,
                      cursor: "default",
                      borderRadius: "4px",
                      "&:focus-visible": { outline: `2px solid ${tokens.brand.border}`, outlineOffset: "2px" },
                    }}
                  >
                    {parts.map((x) => (
                      <Box
                        key={x.name}
                        sx={{
                          width: "54%",
                          maxWidth: 28,
                          // px, not a number: MUI reads a height of 1 as 100%.
                          height: `${Math.max(1, Math.round((x.count / bar.total) * room))}px`,
                          borderRadius: "2px",
                          bgcolor: SUBJECT_FILL[x.tone],
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </Box>
                );
              })}
              {shown && (
                <ChartTooltip
                  x={`${((hover + 0.5) / 12) * 100}%`}
                  y={PLOT - heightOf(shown)}
                  below={PLOT - heightOf(shown) < 120}
                  anchor={hover < 2 ? "start" : hover > 9 ? "end" : "center"}
                  title={shown.full}
                  value={lessonsText(shown.total)}
                  rows={shown.parts.map((x) => ({ key: x.name, label: x.name, value: countText(x.count), color: SUBJECT_FILL[x.tone] }))}
                  note={shown.current ? "Booked so far this month" : null}
                />
              )}
            </Box>
            <Box aria-hidden sx={{ display: "flex", gap: { xs: "4px", sm: "8px" }, mt: "7px" }}>
              {chart.bars.map((bar, i) => (
                <Typography
                  key={bar.key}
                  // A phone has room for every other month: this one and back in twos.
                  sx={{ ...axisSx, flex: 1, minWidth: 0, textAlign: "center", visibility: { xs: i % 2 ? "visible" : "hidden", sm: "visible" } }}
                >
                  {bar.short}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <NotReady state={state} height={469} onRetry={onRetry} />
      )}
    </Box>
  );
};

// A 24×24 flag, loaded one small chunk at a time (R30); a failed load or an unknown code
// leaves the grey box.
const Flag = ({ code }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    setSrc(null);
    const load = FLAGS[code];
    if (!load) return undefined;
    load()
      .then((m) => {
        if (live) setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(m.default)}`);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [code]);
  return (
    <Box
      aria-hidden
      sx={{
        width: 24,
        height: 24,
        boxSizing: "border-box",
        border: `1px solid ${tokens.border.strong}`,
        borderRadius: "4px",
        overflow: "hidden",
        bgcolor: tokens.surface.muted,
        flexShrink: 0,
      }}
    >
      {src && <img alt="" src={src} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
    </Box>
  );
};

// D93: every student account in a ring by gender, the accounts by number of children, and who
// learns (the job's full run counts them; "—" until it has, R37).
export const UsersByGender = ({ state, audience, onRetry }) => {
  const g = state === "ready" ? genderCard(audience) : null;
  let from = 0;
  return (
    <Box component="section" aria-labelledby="reports-gender-title" sx={{ ...cardSx("24px", "24px"), maxWidth: 420 }}>
      <Head id="reports-gender-title" title="Users by gender" subtitle="Drives teacher matching for adult learners" />
      {g ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
            <Box sx={{ position: "relative", width: 148, height: 148, flexShrink: 0 }}>
              <svg width={148} height={148} viewBox="0 0 148 148" aria-hidden focusable="false" style={{ display: "block" }}>
                {g.split.some((s) => s.count > 0) ? (
                  g.split
                    .filter((s) => s.count > 0)
                    .map((s) => {
                      const d = ringSlicePath(74, 74, 51.8, 74, from, from + s.fraction);
                      from += s.fraction;
                      return <path key={s.key} d={d} fill={tokens.chart[s.tone]} />;
                    })
                ) : (
                  <path d={ringSlicePath(74, 74, 51.8, 74, 0, 0.9999)} fill={tokens.chart.track} />
                )}
              </svg>
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: 21, lineHeight: "28px", fontWeight: 600, letterSpacing: "-0.2px", color: tokens.text.primary }}>
                  {g.total}
                </Typography>
                <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
                  {g.unit}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: "12px" }}>
              {g.split.map((s, i) => (
                <Box key={s.key} sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {i > 0 && <Box sx={{ height: "1px", bgcolor: tokens.divider }} />}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: tokens.chart[s.tone], flexShrink: 0 }} />
                    <Typography variant="bodySmall" sx={{ flex: 1, minWidth: 0, color: tokens.text.primary }}>
                      {s.label}
                    </Typography>
                    <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
                      {s.text}
                    </Typography>
                    <Typography variant="bodySmall" sx={{ width: 44, flexShrink: 0, textAlign: "right", color: tokens.text.tertiary }}>
                      {s.share}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography component="h3" variant="bodyMedium" sx={{ color: tokens.text.secondary }}>
              Children per account
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
              {g.children.map((r) => (
                <Box component="li" key={r.key} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Typography variant="bodySmall" sx={{ width: 92, flexShrink: 0, color: tokens.text.primary }}>
                    {r.label}
                  </Typography>
                  <Meter fill={r.fill} color={tokens.chart.blue} />
                  <Typography variant="bodySmallStrong" sx={{ width: 46, flexShrink: 0, textAlign: "right", color: tokens.text.primary }}>
                    {r.text}
                  </Typography>
                  <Typography variant="bodySmall" sx={{ width: 48, flexShrink: 0, textAlign: "right", color: tokens.text.tertiary }}>
                    {r.share}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Typography component="h3" variant="bodyMedium" sx={{ color: tokens.text.secondary }}>
              Who is learning
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {g.learners.map((l) => (
                <Box
                  key={l.key}
                  sx={{
                    border: `1px solid ${tokens.border.subtle}`,
                    borderRadius: "12px",
                    px: "16px",
                    py: "14px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: tokens.chart[l.tone], mt: "8px", flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Typography variant="bodyMediumStrong" sx={{ color: tokens.text.primary }}>
                      {l.label}
                    </Typography>
                    <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
                      {l.note}
                    </Typography>
                  </Box>
                  <Typography variant="bodyMediumStrong" sx={{ color: tokens.text.primary, whiteSpace: "nowrap" }}>
                    {l.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <NotReady state={state} height={240} onRetry={onRetry} />
      )}
    </Box>
  );
};

// D90: the world map, the key, the top five countries (View all lists every one), replacing
// Top countries.
export const UsersByCountry = ({ state, audience, onRetry }) => {
  const [all, setAll] = useState(false);
  const [map, setMap] = useState(null); // null: loading; false: failed; the file once loaded
  const card = useMemo(() => (state === "ready" ? countryCard(audience, all) : null), [state, audience, all]);

  useEffect(() => {
    if (state !== "ready") return;
    let live = true;
    import("../../assets/worldMap.json")
      .then((m) => {
        if (live) setMap(m.default);
      })
      .catch(() => {
        if (live) setMap(false);
      });
    return () => {
      live = false;
    };
  }, [state]);

  const shapes = useMemo(() => (map && card ? map.shapes.filter((s) => !s.dot || card.byCode.has(s.code)) : []), [map, card]);
  const data = useMemo(() => {
    if (!card) return new Map();
    return new Map(
      [...card.byCode].map(([code, hit]) => [code, { fill: tokens.chart.seq[hit.shade - 1], value: hit.users, note: hit.share }])
    );
  }, [card]);
  const fallback = useMemo(
    () => (card ? { fill: tokens.chart.seqEmpty, value: card.nobody.users, note: card.nobody.share } : null),
    [card]
  );

  const wellSx = { bgcolor: tokens.chart.well, borderRadius: "12px", height: { xs: 240, sm: 440 } };

  return (
    <Box component="section" aria-labelledby="reports-countries-title" sx={cardSx("24px", "24px")}>
      <Head
        id="reports-countries-title"
        title="Users by country"
        subtitle={card ? card.subtitle : "Every student account, by the phone's country"}
      />
      {card ? (
        <>
          {map === null && <Box sx={wellSx} />}
          {map === false && (
            <Box sx={{ ...wellSx, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
                The map could not load.
              </Typography>
            </Box>
          )}
          {map && (
            <WorldMap
              map={{ width: map.width, height: map.height, shapes }}
              data={data}
              fallback={fallback}
              label={`Map of ${card.subtitle.split(" · ")[0]}. Every country is listed below.`}
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>
              Fewer users
            </Typography>
            <Box aria-hidden sx={{ display: "flex", borderRadius: "999px", overflow: "hidden" }}>
              {tokens.chart.seq.map((c) => (
                <Box key={c} sx={{ width: 36, height: 10, bgcolor: c }} />
              ))}
            </Box>
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>
              More users
            </Typography>
            <Box sx={{ flex: 1 }} />
            {card.lead && (
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
                {card.lead}
              </Typography>
            )}
          </Box>

          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
            {card.rows.map((r) => (
              <Box component="li" key={r.key} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Flag code={r.key} />
                <Typography
                  variant="bodyMedium"
                  sx={{ flex: "0 1 180px", minWidth: 0, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {r.name}
                </Typography>
                <Meter fill={r.fill} color={tokens.chart.blue} />
                <Typography variant="bodyMediumStrong" sx={{ width: 56, flexShrink: 0, textAlign: "right", color: tokens.text.primary }}>
                  {r.count}
                </Typography>
                <Typography variant="bodySmall" sx={{ width: 50, flexShrink: 0, textAlign: "right", color: tokens.text.secondary }}>
                  {r.share}
                </Typography>
              </Box>
            ))}
          </Box>

          {card.more && (
            <>
              <Box sx={{ height: "1px", bgcolor: tokens.divider }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {card.other && (
                  <>
                    <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
                      {card.other.label}
                    </Typography>
                    <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
                      {card.other.text}
                    </Typography>
                  </>
                )}
                <Box sx={{ flex: 1 }} />
                <ButtonBase
                  onClick={() => setAll((x) => !x)}
                  aria-expanded={all}
                  sx={{ gap: "8px", p: "8px", borderRadius: "8px", color: tokens.text.link, ...focusRing }}
                >
                  <Typography component="span" variant="labelMedium" sx={{ color: "inherit" }}>
                    {all ? "Show top 5" : "View all countries"}
                  </Typography>
                  <RiArrowRightSLine size={18} aria-hidden style={{ transform: all ? "rotate(-90deg)" : "none" }} />
                </ButtonBase>
              </Box>
            </>
          )}
        </>
      ) : (
        <NotReady state={state} height={240} onRetry={onRetry} />
      )}
    </Box>
  );
};
